import { Protocol, PhaseKind } from '../data/protocols';

export interface RunPosition {
  phaseIndex: number;
  cycle: number;
}

export function cycleDurationSec(protocol: Protocol): number {
  return protocol.phases.reduce((sum, p) => sum + p.seconds, 0);
}

export function targetCycles(protocol: Protocol): number {
  if (protocol.cycles) return protocol.cycles;
  if (protocol.durationSec) {
    return Math.round(protocol.durationSec / cycleDurationSec(protocol));
  }
  return 0;
}

export function advance(
  protocol: Protocol,
  cur: RunPosition
): { position: RunPosition; cyclesDone: boolean } {
  const nextPhaseIndex = cur.phaseIndex + 1;
  if (nextPhaseIndex >= protocol.phases.length) {
    const cycle = cur.cycle + 1;
    const cyclesDone = protocol.cycles ? cycle >= protocol.cycles : false;
    return { position: { phaseIndex: 0, cycle }, cyclesDone };
  }
  return { position: { phaseIndex: nextPhaseIndex, cycle: cur.cycle }, cyclesDone: false };
}

export function isCyclesComplete(protocol: Protocol, cycle: number): boolean {
  return protocol.cycles ? cycle >= protocol.cycles : false;
}

export function isTimeComplete(protocol: Protocol, elapsedSec: number): boolean {
  return protocol.durationSec ? elapsedSec >= protocol.durationSec : false;
}

const ENV_LO = 0.05;
const ENV_HI = 0.6;
const ENV_EMPTY = 0.02;

export function breathEnvelope(kind: PhaseKind, progress: number): number {
  const t = Math.min(Math.max(progress, 0), 1);
  switch (kind) {
    case 'inhale':
      return ENV_LO + (ENV_HI - ENV_LO) * t;
    case 'holdIn':
      return ENV_HI;
    case 'exhale':
      return ENV_HI - (ENV_HI - ENV_LO) * t;
    case 'holdOut':
      return ENV_EMPTY;
  }
}

// Normalized lung-fullness curve (0 = empty, 1 = full) used to glide the tone's
// pitch with the breath: rising on the inhale, high at the top hold, falling on
// the exhale, low at the empty hold. Same shape as breathEnvelope, unscaled.
export function pitchEnvelope(kind: PhaseKind, progress: number): number {
  const t = Math.min(Math.max(progress, 0), 1);
  switch (kind) {
    case 'inhale':
      return t;
    case 'holdIn':
      return 1;
    case 'exhale':
      return 1 - t;
    case 'holdOut':
      return 0;
  }
}

// Discrete, low, distinct cue tones for the optional audio-cues layer.
// Descending inhale → hold → exhale reads as a natural settle; empty hold silent.
const CUE_FREQ: Record<PhaseKind, number> = {
  inhale: 246.94, // B3
  holdIn: 196.0, // G3
  exhale: 146.83, // D3
  holdOut: 0, // silent
};

export function cueFrequency(kind: PhaseKind): number {
  return CUE_FREQ[kind];
}
